import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Cake, MessageCircle, Mail } from "lucide-react";
import { EmptyState } from "./CrmUI";
import { BirthdayEmailSettings } from "./BirthdayEmailSettings";
import { getBirthdays, waLink, fmtBday } from "./crmApi";

const WINDOWS = [
    { label: "Next 7 days", value: 7 },
    { label: "Next 30 days", value: 30 },
    { label: "Next 60 days", value: 60 },
    { label: "Next 90 days", value: 90 },
];

export const BirthdaysCRM = () => {
    const [days, setDays] = useState(30);
    const [limit, setLimit] = useState(10);
    const [rows, setRows] = useState(null);

    useEffect(() => {
        (async () => {
            try { setRows(await getBirthdays(days, limit)); }
            catch { toast.error("Could not load birthdays."); setRows([]); }
        })();
    }, [days, limit]);

    return (
        <div data-testid="birthdays-crm">
            <BirthdayEmailSettings />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
                <div className="flex items-center gap-2 flex-1">
                    {WINDOWS.map((w) => (
                        <button key={w.value} onClick={() => setDays(w.value)} className={`px-3.5 py-1.5 rounded-full text-xs font-body border transition-all ${days === w.value ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-oth-ink2 border-oth-line/70 hover:border-oth-ink"}`} data-testid={`bday-window-${w.value}`}>{w.label}</button>
                    ))}
                </div>
                <label className="flex items-center gap-2 text-xs font-body text-oth-ink2">
                    Show
                    <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="bg-white border border-oth-line/70 rounded-lg px-2 py-1.5 focus:outline-none focus:border-oth-blue" data-testid="bday-limit">
                        {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>
            </div>

            {rows === null ? (
                <div className="font-editorial italic text-oth-ink3">Loading…</div>
            ) : rows.length === 0 ? (
                <EmptyState title="No upcoming birthdays" body="Add birthdays to your clients (in the Clients tab or via CSV import) to see them here." testId="bday-empty" />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {rows.map((c, i) => (
                        <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
                            className="flex items-center justify-between rounded-2xl border border-oth-line/70 bg-white px-5 py-4" data-testid={`bday-row-${c.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-full bg-[#FCE9D6] flex items-center justify-center text-[#B5651D] shrink-0"><Cake size={18} /></div>
                                <div className="min-w-0">
                                    <div className="font-display text-sm font-semibold truncate">{c.name}</div>
                                    <div className="text-[12px] text-oth-ink3 font-body">{fmtBday(c.next_birthday)} · {c.days_until === 0 ? "Today 🎉" : `in ${c.days_until} day${c.days_until === 1 ? "" : "s"}`}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                {c.email && <a href={`mailto:${c.email}?subject=${encodeURIComponent("Happy Birthday!")}`} title="Email wish" className="w-9 h-9 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink2 hover:text-oth-blue hover:bg-[#F5F5F2]" data-testid={`bday-email-${c.id}`}><Mail size={15} /></a>}
                                {c.phone && <a href={waLink(c.phone, `Dear ${c.name}, wishing you a very happy birthday from all of us at One Tree Hill Prime Wealth! 🎉`)} target="_blank" rel="noreferrer" title="WhatsApp wish" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full bg-[#0B6B3A] text-white text-xs font-body hover:opacity-90" data-testid={`bday-wa-${c.id}`}><MessageCircle size={14} /> Wish</a>}
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};
